import type { SeriesHostAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import type { HostRole } from './constants'
import SeriesQ from './SeriesQ'
import UserQ from './UserQ'

const SeriesHostQ = {
  ...makeQueryModelV2<SeriesHostAttributes>('series_host')([
    'id',
    'user_id',
    'series_id',
    'deleted_message',
    'deleted_source',
    'request_message',
    'role',
    'feedback_nps',
    'feedback_comment',
    'internal_state',
    'created_at',
    'deleted_at',
    'approved_at',
    'updated_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SeriesHostQ.user_id,
      // Include soft-deleted users by default, since we keep SeriesHost
      // rows even after associated user is deleted.
      paranoid: false,
    },
    Series: {
      query: SeriesQ,
      foreignKey: SeriesQ.id,
      sourceKey: SeriesHostQ.series_id,
    },
  }),

  where: (
    {
      id,
      paranoid = true,
      seriesId,
      userId,
      approved = null,
      role = null,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      paranoid?: boolean
      seriesId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      approved?: boolean | null
      role?: HostRole | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesHostAttributes> => SQL`
            FROM ${SeriesHostQ}
            ${includeAssociationsToSql(SeriesHostQ, include)}
            WHERE
                ${id != null ? SQL`${SeriesHostQ.id} = ${id} AND` : SQL``}
                ${seriesId != null ? SQL`${SeriesHostQ.series_id} = ${seriesId} AND` : SQL``}
                ${userId != null ? SQL`${SeriesHostQ.user_id} = ${userId} AND` : SQL``}
                ${paranoid ? SQL`${SeriesHostQ.deleted_at} IS NULL AND` : SQL``}
                ${
                  approved != null
                    ? SQL`${SeriesHostQ.approved_at} IS ${approved ? SQL`NOT` : SQL``} NULL AND`
                    : SQL``
                }
                ${role != null ? SQL`${SeriesHostQ.role} = ${role} AND` : SQL``}
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,

  isApproved(): SQLFragment<boolean> {
    return SQL`${SeriesHostQ.approved_at} IS NOT NULL`
  },

  hasCompletedPreSeriesSurvey: (): SQLFragment<boolean> =>
    SQL`${SeriesHostQ.internal_state}->'pre_survey_completed_at' IS NOT NULL`,

  hasCompletedPostSeriesSurvey: (): SQLFragment<boolean> =>
    SQL`${SeriesHostQ.internal_state}->'post_survey_completed_at' IS NOT NULL`,
}

export default SeriesHostQ
